import React, { useState } from 'react';
import { toolboxService } from '../../services/api';
import toast from 'react-hot-toast';

const DoctorScheduleGenerator = ({ tool, onBack }) => {
  const [content, setContent] = useState('');
  const [result, setResult] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatResponse = (response) => {
    if (!response) return response;

    try {
      // Try to parse as JSON
      const parsed = JSON.parse(response);
      // If successful, format it with indentation
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If not valid JSON, return as-is
      return response;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setIsSubmitting(true);
    setResult('');

    try {
      const response = await toolboxService.callAgent(content.trim());

      if (response.success) {
        const formattedResponse = formatResponse(response.response || 'No response from agent');
        setResult(formattedResponse);
        toast.success('Agent response received');
      } else {
        setResult(`Error: ${response.error || 'Unknown error'}`);
        toast.error(response.error || 'Failed to get response from agent');
      }
    } catch (error) {
      console.error('Error calling agent:', error);
      const errorMessage = error.message || 'Failed to call agent';
      const formattedError = formatResponse(errorMessage);
      setResult(`Error: ${formattedError}`);
      toast.error('Failed to call agent');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="mb-4 text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Tool Box
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{tool.title}</h1>
        <p className="text-gray-600">{tool.description}</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Enter enquiry once at a time:
            </label>
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setContent('異常出血')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                異常出血
              </button>
              <button
                type="button"
                onClick={() => setContent('腹痛，不舒服')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                腹痛，不舒服
              </button>
              <button
                type="button"
                onClick={() => setContent('多囊卵巢')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                多囊卵巢
              </button>
              <button
                type="button"
                onClick={() => setContent('乳房脹痛，有硬塊，發炎')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                乳房脹痛，有硬塊，發炎
              </button>
              <button
                type="button"
                onClick={() => setContent('產檢')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                產檢
              </button>
              <button
                type="button"
                onClick={() => setContent('產檢，上次月經 2025-11-01')}
                disabled={isSubmitting}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                產檢，上次月經 2025-11-01
              </button>
            </div>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={isSubmitting}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !content.trim()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>

        {result && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Agent Response:</h3>
            <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
              <pre className="whitespace-pre-wrap text-sm text-gray-700">{result}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorScheduleGenerator;

